import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import CtaButtonGroup, {
  CtaActionType,
  ScheduleADemoSourceEnum,
} from "@/components/molecules/CtaButtonGroup";
import { SignInBtn } from "@/components/molecules/SignInBtn";
import { SignUpBtn } from "@/components/molecules/SignUpButton";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import { useI18nContext } from "@/hooks/useI18nContext";
import { UCE_CALENDLY_Affliate, UCE_CALENDLY_LINK } from "@/shared/constants/calendly.constants";
import { MENU_IDS } from "@/shared/constants/menu.constants";
import { SignupCopiesType } from "@/shared/data/navbar.data";
import Amplitude from "@/shared/services/amplitude.service";
import { useMenu } from "@/shared/store/useMenuStore";
import useModalStore, { AuthModalTypeEnum } from "@/shared/store/useModalStore";
import { useNavStore } from "@/shared/store/useNavStore";
import {
  getCurrentURL,
  getReferrer,
  getScreenWidth,
} from "@/shared/utils/commons";
import { getFromLocal } from "@/shared/utils/storage.util";
import { useDataStore } from "@/shared/store/useDataStore";
import { getPathName } from "@/shared/utils/url.util";
import { getUTMFieldsFromLocalStorage } from "@/shared/utils/utm.util";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import { NavItemType, NavMenuItemType, NavbarItemType } from ".";
import { NavbarMenuMweb } from "./NavbarMenuMweb";

export const ROOT_HOST_NEW = `${process.env.NEXT_PUBLIC_ROOT_HOST}` || "https://graphy.com";

const NavbarMweb = ({
  navbarItems,
  signupCopies,
  showBookDemoBtn,
  hideNavCta,
  graphyLogoOnClick,
  registrationButton,
  registrationButtonUS
}: {
  navbarItems: NavbarItemType[];
  signupCopies: SignupCopiesType;
  showBookDemoBtn?: boolean;
  hideNavCta?: boolean;
  graphyLogoOnClick?: any;
  registrationButtonUS?: any;
  registrationButton?: any;
}) => {
  const {
    isVisible: isOpenFeaturesMenu,
    toggleMenu: toggleFeaturesMenu,
    closeMenu: onCloseFeaturesMenu,
  } = useMenu(MENU_IDS.FEATURES);
  const {
    isVisible: isOpenProductsMenu,
    toggleMenu: toggleProductsMenu,
    closeMenu: onCloseProductsMenu,
  } = useMenu(MENU_IDS.PRODUCTS);

  const { showSignUp } = useNavStore();
  // const path = usePathname();


  const handleToggle = (item: NavbarItemType) => {
    if (item.type !== NavItemType.Menu) return;

    const { current_url, utm_params, item_name, screen_width, referrer } =
      LANDING_PAGE_EVENTS.NAV_ITEM_EXPANDED.PROPERTIES;

    if (item.menuType === NavMenuItemType.Features) {
      new Amplitude().sendEvent({
        type: LANDING_PAGE_EVENTS.NAV_ITEM_EXPANDED.LABEL,
        properties: {
          [current_url]: getCurrentURL(),
          [utm_params]: getUTMFieldsFromLocalStorage(),
          [screen_width]: getScreenWidth(),
          [referrer]: getReferrer(),
          [item_name]: "features",
        } as { [key: string]: string },
      });
      toggleFeaturesMenu();
    } else if (item.menuType === NavMenuItemType.Products) {
      new Amplitude().sendEvent({
        type: LANDING_PAGE_EVENTS.NAV_ITEM_EXPANDED.LABEL,
        properties: {
          [current_url]: getCurrentURL(),
          [utm_params]: getUTMFieldsFromLocalStorage(),
          [screen_width]: getScreenWidth(),
          [referrer]: getReferrer(),
          [item_name]: "products",
        } as { [key: string]: string },
      });
      toggleProductsMenu();
    }
  };

  const { openAuthModal, setOpenSignUp } = useModalStore();
  const [openMwebMenu, setOpenMwebMenu] = useState(false);

  const router = useRouter();
  const { query } = router;
  const hasWebinarSuccess = 'webinar_success' in query;

  const path = usePathname();


  const isWebinarPath = path?.includes("/webinar");

  const isAppSumoSignUp = getPathName(path) === "appsumo/signup/";
  // CMS marketing page detection — see Navbar/index.tsx for context. We can no
  // longer infer it from the URL because products/features/marketing pages all
  // share the root path namespace, so the [slug].tsx dispatcher publishes the
  // active CMS slug into the data store.
  const marketingPageSlug = useDataStore((s) => s.marketingPageSlug);
  const isUsLocalePage =
    useI18nContext().locale === "us" && !!marketingPageSlug;
  const isUSLocalAffiliatePage =
    useI18nContext().locale === "us" && marketingPageSlug.startsWith("affiliate");
 

  const openSignInModal = () => {
    const { current_url, screen_width, referrer, source, utm_params, index } =
      LANDING_PAGE_EVENTS.SIGN_IN_TAPPED.PROPERTIES;
    new Amplitude().sendEvent({
      type: LANDING_PAGE_EVENTS.SIGN_IN_TAPPED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [source]: "Navbar - mweb",
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [index]: 0,
      } as { [key: string]: string },
    });
    if (getPathName(path) === "signup/") {
      openAuthModal(AuthModalTypeEnum.SignIn, true);
    } else {
      openAuthModal(AuthModalTypeEnum.SignIn, false);
    }
  };

  const openSignUpModal = () => {
    const { current_url, screen_width, referrer, source, utm_params, index } =
      LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.PROPERTIES;
    new Amplitude().sendEvent({
      type: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [source]: "Navbar - mweb",
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [index]: 0,
      } as { [key: string]: string },
    });
    setOpenSignUp(true);
    if (getPathName(path) === "signup/") {
      openAuthModal(AuthModalTypeEnum.SignUp, true);
    } else {
      openAuthModal(AuthModalTypeEnum.SignUp, false);
    }
  };

  const renderCtaButton = () => {
    // if (isAppSumoSignUp) return null;
    if (isUsLocalePage && !isWebinarPath) {
      return (
        <div>      <CtaButtonGroup
        ctas={[
          {
            type: ButtonTypeEnum.Primary,
            size: ButtonSizeEnum.Regular,
            text: isUSLocalAffiliatePage ? "Talk to an Affiliate Expert" : "Schedule a demo",
            href: isUSLocalAffiliatePage ? `${UCE_CALENDLY_Affliate}` : `${UCE_CALENDLY_LINK}?page_source=hero`,
            actionType: CtaActionType.DelayedHrefBlank,
            amplitudeEvent: {
              eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
              eventProperties: {
                source: ScheduleADemoSourceEnum.navbar,
              },
            },
          },
        ]}
      />
       </div>    );
  }

    if (isWebinarPath) {
      return (
        <div>
        <CtaButtonGroup
          ctas={[
              {
                type: ButtonTypeEnum.Primary,
                size: ButtonSizeEnum.Regular,
                text: isUsLocalePage
                  ? registrationButtonUS?.ctaText ?? "Register now"
                  : registrationButton?.ctaText ?? "Register now",
                href: isUsLocalePage
                  ? registrationButtonUS?.ctaLink ?? ""
                  : registrationButton?.ctaLink ?? "",
                actionType: CtaActionType.DelayedHref,
                amplitudeEvent: {
                  eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
                  eventProperties: {
                    source: ScheduleADemoSourceEnum.navbar,
                  },
                },
              },
            ]}
          />
          </div>
        );
      }
    

    if (showBookDemoBtn) {
      return (
        <>
          {!hideNavCta && (
            <CtaButtonGroup
              ctas={[
                {
                  type: ButtonTypeEnum.Secondary,
                  size: ButtonSizeEnum.Small,
                  text: "Book a Demo",
                  actionType: CtaActionType.BookDemo,
                },
                {
                  type: ButtonTypeEnum.Primary,
                  size: ButtonSizeEnum.Small,
                  text: "Get started",
                  actionType: CtaActionType.SignUp,
                },
              ]}
            />
          )}
        </>
      );
    } else {
      return getPathName(path) === "signup/" ? (
        ''
      ) : (
        <>
          {showSignUp ? (
            <SignInBtn
              text={signupCopies.ctas.SIGN_IN}
              onClick={openSignInModal}
            />
          ) : (
            <SignUpBtn
              text={signupCopies.ctas.SIGN_UP}
              onClick={openSignUpModal}
            />
          )}
        </>
      );
    }
  };

  const actionMenuClick = () => {
    const { current_url, utm_params, screen_width, referrer } =
      LANDING_PAGE_EVENTS.HAMBURGER_TAPPED.PROPERTIES;

    new Amplitude().sendEvent({
      type: LANDING_PAGE_EVENTS.HAMBURGER_TAPPED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
      } as { [key: string]: string },
    });
    setOpenMwebMenu(!openMwebMenu);
  };

  const closeMwebNavbar = () => {
    setOpenMwebMenu(false);
  };

  const containsNavbarItems = useMemo(
    () => navbarItems.length > 0,
    [navbarItems.length]
  );

  return (
    <>
      <nav
        className={`backdrop-blur-md ${
          openMwebMenu ? "bg-white" : "bg-white30"
        }`}
      >
        <div className=" flex items-center justify-between px-4 py-3 md:hidden">
          {containsNavbarItems && (
            <Image
              src={
                openMwebMenu
                  ? "/images/close-menu.svg"
                  : "/images/hamburger.svg"
              }
              width={14}
              height={12}
              alt="menu-toggle"
              className="cursor-pointer"
              onClick={actionMenuClick}
            />
          )}
          <Link
            href={graphyLogoOnClick ? "" : ROOT_HOST_NEW}
            className={classNames({
              "absolute left-1/2 -translate-x-1/2": containsNavbarItems,
            })}
            onClick={(e) => {
              e.preventDefault();
              if (graphyLogoOnClick) {
                window.open(graphyLogoOnClick.href, "_blank");
                new Amplitude().sendEvent({
                  type: graphyLogoOnClick.amplitudeEvent?.eventName,
                  properties: {
                    ...graphyLogoOnClick.amplitudeEvent?.eventProperties,
                    current_url: getCurrentURL(),
                    utm_params: getFromLocal("utmFields"),
                    screen_width: getScreenWidth(),
                    referrer: getReferrer(),
                  } as { [key: string]: string | number },
                });
              }
            }}
          >
            <Image
              src="/images/graphy_logo_black.svg"
              width={63}
              height={18}
              alt="graphy"
            />
          </Link>

          {renderCtaButton()}
        </div>
        {openMwebMenu && (
          <menu className="md:hidden">
            <ul className="flex w-full flex-col items-center gap-2  rounded-3xl border-b px-4 py-2">
              {navbarItems.map((item, index) => {
                if (item.type === NavItemType.Link) {
                  return (
                    <>
                      {item?.link && (
                        <Link
                          href={item.link}
                          key={"nav" + item.name + index}
                          className="py-3 align-middle text-caption font-medium text-grey-3"
                        >
                          {item.name}
                        </Link>
                      )}
                    </>
                  );
                } else {
                  return (
                    <>{item?.menuItems && <NavbarMenuMweb
                      label={item.name}
                      key={"navbar-menu" + item.name + index}
                      menuItems={item?.menuItems}
                      closeMwebNavbar={closeMwebNavbar}
                      onClose={
                        item.menuType === NavMenuItemType.Features
                          ? onCloseFeaturesMenu
                          : onCloseProductsMenu
                      }
                      handleToggle={() => handleToggle(item)}
                      isOpen={
                        item.menuType === NavMenuItemType.Features
                          ? isOpenFeaturesMenu
                          : isOpenProductsMenu
                      }
                    /> }</>
                    
                  );
                }
              })}
            </ul>
          </menu>
        )}
      </nav>
    </>
  );
};

export default NavbarMweb;
